import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { UserPlus, Check, Clock, X, User, Users, Search } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

function Members() {
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [loadingConnections, setLoadingConnections] = useState({});
  const { t } = useLanguage();
  const currentUser = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    const init = async () => {
      await fetchDepartments();
      await fetchMembers();
      await fetchAllConnectionStatuses();
    };
    init();
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [selectedDept]);

  const fetchDepartments = async () => {
    try {
      const { data } = await api.get('/departments');
      setDepartments(data);
    } catch (err) {
      console.error('Failed to fetch departments');
    }
  };

  const fetchMembers = async () => {
    try {
      const url = selectedDept ? `/users?department_id=${selectedDept}` : '/users';
      const { data } = await api.get(url);
      if (Array.isArray(data)) {
        setMembers(data);
      }
    } catch (err) {
      console.error('Failed to fetch members');
    }
  };

  const fetchAllConnectionStatuses = async () => {
    try {
      // Fetch all connections where I am involved (accepted or pending)
      const { data } = await api.get('/connections');

      const statusMap = {};
      if (Array.isArray(data)) {
        data.forEach(conn => {
          // Identify the other user in the connection
          const otherUserId = conn.id;

          let status;
          if (conn.status === 'accepted') {
            status = 'connected';
          } else if (conn.requester_id === currentUser.id) {
            status = 'pending_sent';
          } else {
            status = 'pending_received';
          }

          statusMap[otherUserId] = {
            status,
            connectionId: conn.connection_id
          };
        });
      }
      setConnectionStatuses(statusMap);
    } catch (err) {
      console.error('Failed to fetch connection statuses');
    }
  };

  const sendConnectionRequest = async (receiverId) => {
    setLoadingConnections(prev => ({ ...prev, [receiverId]: true }));
    try {
      const { data } = await api.post('/connections/request', { receiver_id: receiverId });
      setConnectionStatuses(prev => ({
        ...prev,
        [receiverId]: { status: 'pending_sent', connectionId: data.connectionId }
      }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send request');
    }
    setLoadingConnections(prev => ({ ...prev, [receiverId]: false }));
  };

  const cancelRequest = async (connectionId, userId) => {
    setLoadingConnections(prev => ({ ...prev, [userId]: true }));
    try {
      await api.post('/connections/reject', { connection_id: connectionId });
      setConnectionStatuses(prev => {
        const newStatuses = { ...prev };
        delete newStatuses[userId];
        return newStatuses;
      });
    } catch (err) {
      alert('Failed to cancel request');
    }
    setLoadingConnections(prev => ({ ...prev, [userId]: false }));
  };

  const acceptRequest = async (connectionId, userId) => {
    setLoadingConnections(prev => ({ ...prev, [userId]: true }));
    try {
      await api.post('/connections/accept', { connection_id: connectionId });
      setConnectionStatuses(prev => ({
        ...prev,
        [userId]: { status: 'connected', connectionId }
      }));
    } catch (err) {
      console.error('Accept failure:', err);
      alert(err.response?.data?.error || 'Failed to accept request');
    }
    setLoadingConnections(prev => ({ ...prev, [userId]: false }));
  };

  const filteredMembers = members
    .filter(m => m.id !== currentUser.id)
    .filter(m =>
      (m.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (m.username?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (m.role_title?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

  const isOnline = (lastActive) => {
    if (!lastActive) return false;
    const lastActiveDate = new Date(lastActive);
    const now = new Date();
    // Consider online if active in the last 5 minutes (300,000 ms)
    return (now - lastActiveDate) < 5 * 60 * 1000;
  };

  const formatLastActive = (lastActive) => {
    if (!lastActive) return t('never_label');
    const date = new Date(lastActive);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderConnectionButton = (member) => {
    // Don't show button for self
    if (member.id === currentUser.id) return null;

    const status = connectionStatuses[member.id];
    const isLoading = loadingConnections[member.id];

    if (isLoading) {
      return (
        <button className="btn-cyber" disabled style={{ width: '100%', fontSize: '0.8rem', padding: '0.75rem', opacity: 0.5 }}>
          <Clock size={16} style={{ marginRight: '0.5rem' }} />
          {t('processing')}
        </button>
      );
    }


    if (!status) {
      return (
        <button
          onClick={() => sendConnectionRequest(member.id)}
          className="btn-cyber"
          style={{ width: '100%', fontSize: '0.8rem', padding: '0.75rem' }}
        >
          <UserPlus size={16} style={{ marginRight: '0.5rem' }} />
          {t('connect')}
        </button>
      );
    }

    switch (status.status) {
      case 'connected':
        return (
          <Link
            to={`/profile/${member.id}`}
            className="btn-cyber"
            style={{ width: '100%', fontSize: '0.8rem', padding: '0.75rem', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(34, 211, 238, 0.1)', border: '1px solid var(--accent-cyan)', color: 'var(--accent-cyan)' }}
          >
            <Check size={16} style={{ marginRight: '0.5rem' }} />
            {t('connected')}
          </Link>
        );
      case 'pending_sent':
        return (
          <button
            onClick={() => cancelRequest(status.connectionId, member.id)}
            className="btn-cyber"
            style={{ width: '100%', fontSize: '0.8rem', padding: '0.75rem', background: 'rgba(139, 92, 246, 0.9)', border: '1px solid var(--accent-violet)' }}
          >
            <Clock size={16} style={{ marginRight: '0.5rem' }} />
            {t('pending_cancel')}
          </button>
        );
      case 'pending_received':
        return (
          <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
            <button
              onClick={() => acceptRequest(status.connectionId, member.id)}
              className="btn-cyber"
              style={{ flex: 1, fontSize: '0.8rem', padding: '0.75rem' }}
            >
              <Check size={16} />
            </button>
            <button
              onClick={() => cancelRequest(status.connectionId, member.id)}
              className="btn-cyber"
              style={{ flex: 1, fontSize: '0.8rem', padding: '0.75rem', background: 'rgba(244, 114, 182, 0.1)', border: '1px solid var(--accent-magenta)' }}
            >
              <X size={16} />
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container" style={{ paddingBottom: '4rem' }}>
      <style>{`
        .directory-card {
          position: relative;
          background: rgba(255,255,255,0.015);
          border: 1px solid var(--glass-border);
          border-radius: 24px;
          padding: 2.5rem 1.5rem;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          overflow: hidden;
        }
        .directory-card:hover {
          transform: translateY(-8px);
          border-color: var(--accent-primary)40;
          box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.6), var(--shadow-inset);
          background: rgba(255,255,255,0.025);
        }
        .directory-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; height: 100%;
          background: radial-gradient(circle at top right, var(--accent-primary)10, transparent 70%);
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
        }
        .directory-card:hover::before {
          opacity: 1;
        }
        .avatar-pod {
          width: 100px;
          height: 100px;
          border-radius: 28px;
          padding: 4px;
          background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02));
          border: 1px solid var(--glass-border);
          position: relative;
          margin-bottom: 1.5rem;
          box-shadow: var(--shadow-apex);
          transition: transform 0.4s ease;
        }
        .directory-card:hover .avatar-pod {
          transform: scale(1.05) rotate(2deg);
        }
        .control-bar {
          background: rgba(255,255,255,0.015);
          border: 1px solid var(--glass-border);
          padding: 1.25rem 2rem;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 3rem;
          backdrop-filter: blur(10px);
        }
      `}</style>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '3.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '0.6rem' }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '12px',
            background: 'linear-gradient(135deg, var(--accent-primary), rgba(139,92,246,0.8))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px -6px rgba(99,102,241,0.5)',
            border: '1px solid rgba(255,255,255,0.2)',
            transform: 'rotate(-4deg)'
          }}>
            <Users size={22} color="white" />
          </div>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--text-heading)', letterSpacing: '-0.04em', lineHeight: 1.1 }}>
            {t('member_directory').split(' ')[0]} <span style={{
              background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-cyan))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>{t('member_directory').split(' ').slice(1).join(' ')}</span>
          </h2>
        </div>
        <p style={{ color: 'var(--text-muted)', fontWeight: 500, fontSize: '1rem', letterSpacing: '0.01em', opacity: 0.8 }}>{t('member_directory_desc')}</p>
      </div>

      {/* ── Unified Glass Control Bar ───────────────────────────────────── */}
      <div className="control-bar stack-mobile">
        <div style={{ position: 'relative', flex: 1, minWidth: '300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '1.25rem', top: '50%', transform: 'translateY(-50%)', opacity: 0.4, color: 'var(--accent-primary)' }} />
          <input
            className="cyber-input"
            style={{ width: '100%', paddingLeft: '3.5rem', borderRadius: '14px', background: 'rgba(0,0,0,0.2)' }}
            placeholder={t('search_members_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <select
            className="cyber-input"
            style={{ minWidth: '200px', padding: '0.85rem 3rem 0.85rem 1.25rem', borderRadius: '14px', background: 'rgba(0,0,0,0.2)' }}
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
          >
            <option value="">{t('all_departments')}</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>

          <div style={{ 
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem', 
            padding: '0.4rem 1rem', background: 'rgba(255,255,255,0.03)', 
            borderRadius: '999px', border: '1px solid rgba(255,255,255,0.04)' 
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {filteredMembers.length} INDEXED
            </span>
          </div>
        </div>
      </div>

      {/* ── Member Grid ─────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '2rem'
      }}>
        {filteredMembers.map(member => (
          <div key={member.id} className="directory-card animate-fade">
            <Link to={`/profile/${member.id}`} style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <div className="avatar-pod">
                <div style={{ width: '100%', height: '100%', borderRadius: '24px', overflow: 'hidden' }}>
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt={member.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, var(--bg-surface), var(--bg-card))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem', fontWeight: 900, color: 'var(--accent-primary)' }}>
                      {member.username[0].toUpperCase()}
                    </div>
                  )}
                </div>
              </div>

              <h3 style={{ fontSize: '1.25rem', color: 'var(--text-heading)', fontWeight: 800, marginBottom: '0.2rem', letterSpacing: '-0.02em' }}>
                {member.full_name || member.username}
              </h3>
              <div style={{ 
                fontSize: '0.72rem', fontWeight: 900, color: 'var(--accent-primary)', 
                textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1.25rem' 
              }}>
                {member.role_title || 'AP Member'}
              </div>
            </Link>

            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '2rem', minHeight: '28px' }}>
              {member.skills && JSON.parse(member.skills).slice(0, 3).map((skill, idx) => (
                <span key={idx} style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid var(--glass-border)',
                  padding: '0.3rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.68rem',
                  color: 'var(--text-muted)',
                  fontWeight: 700,
                  textTransform: 'uppercase'
                }}>{skill}</span>
              ))}
            </div>

            <div style={{ width: '100%', marginTop: 'auto' }}>
              {renderConnectionButton(member)}
            </div>
          </div>
        ))}
      </div>

      {filteredMembers.length === 0 && (
        <div style={{ padding: '6rem', textAlign: 'center', background: 'rgba(255,255,255,0.01)', border: '1px dashed var(--glass-border)', borderRadius: '24px' }}>
          <User size={48} style={{ opacity: 0.1, marginBottom: '1rem' }} />
          <h3 style={{ color: 'var(--text-muted)', fontSize: '1.1rem', fontWeight: 600 }}>{t('no_members_found')}</h3>
        </div>
      )}
    </div>
  );
}

export default Members;
